'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { FLYOUT_LAYERS, type FlyoutPanel } from './useFlyoutPanel';
import styles from './portalFlyout.module.scss';

interface PortalFlyoutProps {
  flyout: FlyoutPanel;
  id: string;
  ariaLabel: string;
  ariaLabelledBy: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Renders a caret + panel into document.body, anchored to a flyout's trigger.
 * Pairs with useFlyoutPanel — caller supplies open state and refs via `flyout`.
 */
export function PortalFlyout({
  flyout,
  id,
  ariaLabel,
  ariaLabelledBy,
  className = '',
  children,
}: PortalFlyoutProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !flyout.isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: FLYOUT_LAYERS.overlay }}
      aria-hidden={false}
      onMouseEnter={flyout.cancelHoverClose}
      onMouseLeave={flyout.scheduleHoverClose}
    >
      <div
        ref={flyout.panelRef}
        id={id}
        role="menu"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        onMouseEnter={flyout.panelProps.onMouseEnter}
        onMouseLeave={flyout.panelProps.onMouseLeave}
        onKeyDown={flyout.panelProps.onKeyDown}
        className={`${styles.panel} ${className}`}
        style={{
          zIndex: FLYOUT_LAYERS.panel,
          maxHeight: flyout.panelMaxHeight,
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
